/**
 * Where a portalled menu sits relative to the control that opened it.
 *
 * The menu is rendered into the document body so that no ancestor's overflow
 * can clip it, which means it has to work out its own place on screen. Kept
 * apart from the component because it is the part with real edge cases —
 * flipping, clamping — and those are worth testing without a DOM.
 *
 * All values are viewport coordinates, as `getBoundingClientRect` reports
 * them, and the result is used directly as `position: fixed` offsets.
 */

export interface MenuRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface MenuPositionInput {
  /** The trigger's rectangle. */
  anchor: MenuRect;
  /** The menu's own measured size. */
  menu: Pick<MenuRect, "width" | "height">;
  viewport: { width: number; height: number };
  align: "start" | "center" | "end";
  /** Gap between trigger and menu. */
  sideOffset: number;
  /** Smallest gap left against any viewport edge. */
  margin?: number;
}

export interface MenuPosition {
  top: number;
  left: number;
  /** True when there was no room below and the menu was placed above. */
  flipped: boolean;
}

export function computeMenuPosition({
  anchor,
  menu,
  viewport,
  align,
  sideOffset,
  margin = 8,
}: MenuPositionInput): MenuPosition {
  const anchorBottom = anchor.top + anchor.height;
  const spaceBelow = viewport.height - anchorBottom;

  /* Prefer below. Flip only when below genuinely cannot hold the menu AND
     above has more room, so a menu taller than the whole viewport does not
     jump upward and lose its top edge instead of its bottom. */
  const flipped = spaceBelow < menu.height + sideOffset + margin && anchor.top > spaceBelow;
  const idealTop = flipped ? anchor.top - menu.height - sideOffset : anchorBottom + sideOffset;

  const anchorRight = anchor.left + anchor.width;
  let idealLeft: number;
  if (align === "start") idealLeft = anchor.left;
  else if (align === "center") idealLeft = anchor.left + anchor.width / 2 - menu.width / 2;
  else idealLeft = anchorRight - menu.width;

  const clamp = (value: number, max: number) =>
    Math.max(margin, Math.min(value, Math.max(margin, max)));

  return {
    top: clamp(idealTop, viewport.height - menu.height - margin),
    left: clamp(idealLeft, viewport.width - menu.width - margin),
    flipped,
  };
}
